import type { Attachment } from '@zg-rentals/satellite-components';
import {
  SatelliteAttachmentMetadataStatusType,
  type SatelliteWebLandlordGetAttachmentsMetadataDto,
  type SatelliteWebLandlordGetAttachmentsMetadataResponseDto,
} from '@zg-rentals/ts-satellite-client';

/**
 * Map Proxit API attachmentMetadataStatus (e.g. 'COMPLETE') to the string
 * expected by satellite-components hasMissingAttachmentContent ('complete').
 * Without this, the sidebar download/preview icon stays disabled.
 */
function mapToAttachmentMetadataStatus(
  status: SatelliteWebLandlordGetAttachmentsMetadataDto['attachmentMetadataStatus'],
): string {
  return status === SatelliteAttachmentMetadataStatusType.COMPLETE ? 'complete' : status;
}

/**
 * Map getAttachmentsMetadata API response (attachmentMetadataDtos) to the Attachment
 * shape expected by AttachmentCard and other UI components.
 */
export function mapAttachmentMetadataDtoToAttachment(dto: SatelliteWebLandlordGetAttachmentsMetadataDto): Attachment {
  return {
    attachmentAddedMs: String(dto.attachmentAddedMs),
    attachmentMetadataStatus: mapToAttachmentMetadataStatus(dto.attachmentMetadataStatus),
    downloadUrl: dto.downloadUrl,
    fileName: dto.fileName,
    fileSizeBytes: dto.fileSizeBytes,
    fileType: dto.fileType,
    isAttachmentDeleted: dto.isAttachmentDeleted,
    isAttachmentExpired: dto.isAttachmentExpired,
    previewUrl: dto.previewUrl,
    zDocId: dto.zdocId,
  };
}

/**
 * Map Attachment (e.g. from a sent message) to getAttachmentsMetadata DTO shape.
 * Used for optimistic cache updates when adding a message with attachments.
 */
export function mapAttachmentToMetadataDto(attachment: Attachment): SatelliteWebLandlordGetAttachmentsMetadataDto {
  const attachmentAddedMs = attachment.attachmentAddedMs != null ? Number(attachment.attachmentAddedMs) : Date.now();
  return {
    attachmentAddedMs: Number.isNaN(attachmentAddedMs) ? Date.now() : attachmentAddedMs,
    attachmentMetadataStatus: SatelliteAttachmentMetadataStatusType.COMPLETE,
    fileName: attachment.fileName ?? '',
    fileSizeBytes: attachment.fileSizeBytes ?? 0,
    fileType: attachment.fileType ?? '',
    isAttachmentDeleted: attachment.isAttachmentDeleted ?? false,
    isAttachmentExpired: attachment.isAttachmentExpired ?? false,
    downloadUrl: attachment.downloadUrl ?? undefined,
    previewUrl: attachment.previewUrl ?? undefined,
    zdocId: attachment.zDocId ?? undefined,
  };
}

/**
 * Flag attachments as deleted in a cached getAttachmentsMetadata response. Optimistic
 * patch for attachment deletion: a refetch fired right after the mutation can race the
 * backend's deletion propagation and re-cache the attachment as live, so the cache is
 * patched directly instead of invalidated.
 */
export function markAttachmentMetadataDeleted(
  existing: SatelliteWebLandlordGetAttachmentsMetadataResponseDto | undefined,
  zdocIds: Array<string>,
): SatelliteWebLandlordGetAttachmentsMetadataResponseDto | undefined {
  if (!existing) return existing;
  const deletedIds = new Set(zdocIds);
  return {
    attachmentMetadataDtos: existing.attachmentMetadataDtos.map((dto) =>
      dto.zdocId && deletedIds.has(dto.zdocId) ? { ...dto, isAttachmentDeleted: true } : dto,
    ),
  };
}
